// Blåtur membership: the invitation list, absences and strikes, and applications to be readmitted.
// The strike rules themselves live in strikes.js.

import {
  getFirestore, collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, serverTimestamp, deleteField,
} from 'firebase/firestore';
import { app } from './firebase-config';
import { normalizeName } from './adminData';

export { STRIKES_TO_OUT, memberStatus, historicalAbsences } from './strikes';

const db = getFirestore(app);

const nowIso = () => new Date().toISOString();

export async function loadMembers() {
  const snap = await getDocs(collection(db, 'blatur_members'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => a.name.localeCompare(b.name, 'no'));
}

export async function addMembers(names, addedBy) {
  for (const name of names) {
    await addDoc(collection(db, 'blatur_members'), {
      name: name.trim(), absences: [], readmissions: [], addedBy, addedAt: nowIso(),
    });
  }
}

export const removeMember = (member) => deleteDoc(doc(db, 'blatur_members', member.id));

export async function registerAbsence(member, { year, reason, strike }, registeredBy) {
  const absences = [...(member.absences || []), { year: Number(year), reason: reason.trim(), strike: !!strike, registeredBy, at: nowIso() }];
  await updateDoc(doc(db, 'blatur_members', member.id), { absences });
}

// Several absences at once, e.g. when starting to practise the rule on past trips
export async function registerAbsences(entries, registeredBy) {
  const byMember = new Map();
  entries.forEach(({ member, year, reason, strike }) => {
    const list = byMember.get(member) || [...(member.absences || [])];
    list.push({ year: Number(year), reason, strike: !!strike, registeredBy, at: nowIso() });
    byMember.set(member, list);
  });
  for (const [member, absences] of byMember) {
    await updateDoc(doc(db, 'blatur_members', member.id), { absences });
  }
}

export async function removeAbsence(member, index) {
  const absences = (member.absences || []).filter((_, i) => i !== index);
  await updateDoc(doc(db, 'blatur_members', member.id), { absences });
}

export async function readmit(member, { penalty, applicationId }, decidedBy) {
  const clearsUntil = Math.max(0, ...(member.absences || []).filter(a => a.strike).map(a => a.year));
  const readmissions = [...(member.readmissions || []), {
    penalty: (penalty || '').trim(), clearsUntil, decidedBy, at: nowIso(), ...(applicationId ? { applicationId } : {}),
  }];
  await updateDoc(doc(db, 'blatur_members', member.id), { readmissions });
}

// ── Applications ──

export async function applyForReadmission({ name, uid, text }) {
  await addDoc(collection(db, 'blatur_applications'), { name, uid, text: text.trim(), status: 'pending', createdAt: serverTimestamp() });
}

export async function loadMyApplications(uid) {
  const snap = await getDocs(query(collection(db, 'blatur_applications'), where('uid', '==', uid)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function loadApplications() {
  const snap = await getDocs(collection(db, 'blatur_applications'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function decideApplication(application, { approved, penalty }, decidedBy, members) {
  await updateDoc(doc(db, 'blatur_applications', application.id), {
    status: approved ? 'approved' : 'rejected', penalty: penalty || '', decidedBy, decidedAt: nowIso(),
  });
  if (approved) {
    const member = members.find(m => normalizeName(m.name) === normalizeName(application.name));
    if (member) await readmit(member, { penalty, applicationId: application.id }, decidedBy);
    else await addMembers([application.name], decidedBy);
  }
}

// ── E-mail addresses ──
// Stored in emails/{uid} (only readable by the owner and blåtur admins), kept in sync on login

export async function loadEmailsByName(directory) {
  const snap = await getDocs(collection(db, 'emails'));
  const byUid = Object.fromEntries(snap.docs.map(d => [d.id, d.data().email]));
  const byName = {};
  directory.users.forEach(u => { if (byUid[u.id]) byName[normalizeName(u.name)] = byUid[u.id]; });
  directory.people.forEach(p => { if (p.email && !byName[normalizeName(p.name)]) byName[normalizeName(p.name)] = p.email; });
  return byName;
}

// ── Aspirants (new people proposed for blåtur, see aspirants.js) ──

export async function loadAspirants() {
  const snap = await getDocs(collection(db, 'blatur_aspirants'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.nominatedAt || '').localeCompare(b.nominatedAt || ''));
}

export async function nominateAspirant(name, user, userName) {
  await addDoc(collection(db, 'blatur_aspirants'), {
    name: name.trim(),
    nominatedBy: user.uid,
    nominatedByName: userName,
    nominatedAt: nowIso(),
    status: 'aspirant',
    vetoes: {},
  });
}

export async function setVeto(aspirant, user, userName, on) {
  await updateDoc(doc(db, 'blatur_aspirants', aspirant.id), {
    [`vetoes.${user.uid}`]: on ? { name: userName, at: nowIso() } : deleteField(),
  });
}

export const withdrawAspirant = (aspirant) => deleteDoc(doc(db, 'blatur_aspirants', aspirant.id));

// Admin: put the aspirant on the invitation list. overrideVeto records that existing vetoes were overruled.
export async function admitAspirant(aspirant, { overrideVeto }, admittedBy) {
  await addMembers([aspirant.name], admittedBy);
  await updateDoc(doc(db, 'blatur_aspirants', aspirant.id), {
    status: 'admitted',
    admittedBy,
    admittedAt: nowIso(),
    ...(overrideVeto ? { overrideVeto: true } : {}),
  });
}
